import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';

export interface UpdateTrucksResult {
  unassignIds: number[];
  assignIds: number[];
}

@Component({
  selector: 'app-update-trucks-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, MatListModule],
  templateUrl: './update-trucks-dialog.component.html',
  styleUrls: ['./update-trucks-dialog.component.scss']
})
export class UpdateTrucksDialogComponent {
  assignedTrucks: any[];
  availableTrucks: any[];
  minRequiredWeight: number = 0;

  unassignIds: number[] = [];
  newTruckIds: number[] = [];
  searchText: string = '';

  constructor(
    public dialogRef: MatDialogRef<UpdateTrucksDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { order: any; availableTrucks: any[] }
  ) {
    this.assignedTrucks = [...data.order.assignedTrucks];
    this.availableTrucks = data.availableTrucks;
    this.minRequiredWeight = this.getMinRequiredWeight(data.order?.remainingByType);
  }

  get filteredAvailable() {
    return this.availableTrucks.filter(t =>
      t.name.toLowerCase().includes(this.searchText.toLowerCase())
    );
  }

  private parseTruckWeight(name: string): number {
    const match = (name || '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : Infinity;
  }

  private getMinRequiredWeight(remainingByType: { truckType: string; remaining: number }[] | undefined): number {
    if (!remainingByType || remainingByType.length === 0) return 0;
    const weights = remainingByType
      .filter(r => r.remaining > 0)
      .map(r => this.parseTruckWeight(r.truckType));
    return weights.length > 0 ? Math.min(...weights) : 0;
  }

  isUndersized(truck: any): boolean {
    if (!this.minRequiredWeight) return false;
    return this.parseTruckWeight(truck.truckTypeName) < this.minRequiredWeight;
  }

  toggleUnassign(truckId: number): void {
    const idx = this.unassignIds.indexOf(truckId);
    if (idx >= 0) {
      this.unassignIds.splice(idx, 1);
      // drop any excess new selections that no longer fit the freed slots
      this.newTruckIds = this.newTruckIds.slice(0, this.unassignIds.length);
    } else {
      this.unassignIds.push(truckId);
    }
  }

  isMarkedForUnassign(truckId: number): boolean {
    return this.unassignIds.includes(truckId);
  }

  get availableSlots(): number {
    return this.unassignIds.length - this.newTruckIds.length;
  }

  get newLimitReached(): boolean {
    return this.availableSlots <= 0;
  }

  isNewDisabled(truckId: number): boolean {
    if (this.newTruckIds.includes(truckId)) return false;
    const truck = this.availableTrucks.find(t => t.id === truckId);
    if (truck && this.isUndersized(truck)) return true;
    return this.newLimitReached;
  }

  onConfirm(): void {
    const result: UpdateTrucksResult = {
      unassignIds: this.unassignIds,
      assignIds: this.newTruckIds
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  get hasChanges(): boolean {
    return this.unassignIds.length > 0 || this.newTruckIds.length > 0;
  }
}
